/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import uniq from 'lodash.uniq';
import {
  IApplicationFeatures
} from '@ion/core/src/security/ApplicationFeatures';
import {
  createEnabledFeatures,
  getRoleFeatures
} from '@ion/core/src/security/helpers';
import {
  ApplicationFeature,
  GenericObject,
  Link,
  Role
} from '@ion/core/src/types/entities';
import {createModuleActions, MAP_MODULES_FEATURE_IDS} from './ModuleActionHelpers';

export enum ACTIONS {
  ALL = 'All',
  CREATE = 'Create',
  READ = 'Read',
  UPDATE = 'Update',
  DELETE = 'Delete',
  ASSIGN = 'Assign'
};

/**
 * Defines the default actions for list of ADM Features
 * to choose while create/update role.
 */
export const defaultModuleActions = [
  ACTIONS.ALL,
  ACTIONS.CREATE,
  ACTIONS.READ,
  ACTIONS.UPDATE,
  ACTIONS.DELETE,
  ACTIONS.ASSIGN
];

/**
 * @generateFeatureSet
 *
 * The generateFeatureSet method takes the list fo application Features, and an
 * optional Role (when in edit mode), and creates the set of Features which will
 * be rendered, setting the enabled state of each Feature in the combined list
 * based on the Features the Role has access to.
 *
 * @param    {ApplicationFeature[]} features A collection of all current Features.
 * @param    {Role} role Only applicable when in edit mode, in which case the
 *           current Role being edited is inspected to determine which Features
 *           are available.
 * @returns  {ApplicationFeature[]} The Array of Features which are to be rendered.
 */
export const generateFeatureSet = (
  role: Role, api: IApplicationFeatures, readOnly = false): ApplicationFeature[] => {
  const features: ApplicationFeature[] = api.getAllApplicationFeatures();
  const isEditMode = !isUndefined(role);
  const isModuleActionsRequired = readOnly;
  const roleFeatures = isEditMode ? getRoleFeatures(role, features) : null;
  const enabledFeatures = isEditMode ? createEnabledFeatures(roleFeatures) : {};
  const enabledModules = (isEditMode && isModuleActionsRequired)
    ? createEnabledModules({features: roleFeatures, api: api}) : {};
  const featureSet = [];
  const prepareFeatures = (features: ApplicationFeature[], isChild?: boolean) => {
    features.forEach((feature: ApplicationFeature) => {
      const children = feature.features;
      const isEnabled = enabledFeatures[feature.id] === true;
      const isAllowed = readOnly || api.has(feature.id);
      const modules = enabledModules ? enabledModules[feature.id] : [];
      if (!isChild) {
        featureSet.push({
          ...feature,
          enabled : isEnabled,
          allowed : isAllowed
        });
        if (modules?.length) {
          featureSet[featureSet.length - 1].modules = modules;
        }
      } else {
        feature.enabled = isEnabled;
        feature.allowed = isAllowed;
        if (modules?.length) {
          feature.modules = modules;
        }
      }
      if (children?.length) {
        prepareFeatures(children, true);
      }
    });
  };
  prepareFeatures(features);
  return featureSet;
};

/**
 *
 * Given a list of Role Feature Modules, recursively iterates each modules and adds the id
 * of the Feature as a key to a map with a  value of true to indicate that the feature
 * exists and is available as a Role Feature Module within the current list Features.
 *
 * This function is used when editing a Role as it generates a map of the roles feature
 * module ids which are subsequently used to determine which features in the full list of
 * application features module should be enabled.
 *
 * The createModuleActions method will be executed while update the modules for the existing Role
 * which we have created under DOOC/DOCTC.
 */
export const createEnabledModules = ({
  features,
  api
}, parentId?: string, enabled: GenericObject = {}
): GenericObject => {
  features.forEach(({id, features, modules}: ApplicationFeature) => {
    enabled[id] = modules || createModuleActions({
      Features          : features,
      moduleName        : MAP_MODULES_FEATURE_IDS[id],
      actions           : defaultModuleActions,
      api               : api,
      enableActionAlias : true
    });
    if (parentId && enabled[id]) {
      enabled[parentId] = modules;
    }
    if (features?.length) {
      createEnabledModules({features, api}, id, enabled);
    }
  });
  return enabled;
};

/**
 * @getFeaturesMap
 *
 * Provides a utility which flattens all enabled features and nested features to an
 * object where the feature id is the key and the feature is the value.
 */
export const getFeaturesMap = (
  items: ApplicationFeature[] = [], enabledOnly = true, map: GenericObject = {}): GenericObject => {
  items.forEach((item: ApplicationFeature) => {
    if (!enabledOnly || item.enabled) {
      item.enabled = item.allowed;
      map[item.id] = item;
      if (item.features) {
        getFeaturesMap(item.features, enabledOnly, map);
      }
    }
  });
  return map;
};

/**
 * @enableFeatures
 *
 * Provides a utility which flattens all nested features and sets their enabled property
 * to true. This utility is generally only used by a Feature component to auto-enable
 * all if it's sub-features.
 */
export const enableFeatures = (features: ApplicationFeature[]): ApplicationFeature[] => {
  let flattened: ApplicationFeature[] = [];
  const flat = (items: ApplicationFeature[]) => {
    items.forEach((feature: ApplicationFeature) => {
      const children = feature.features;
      feature.enabled = feature.allowed;
      flattened = flattened.concat(feature);
      if (children?.length) {
        flat(children);
      }
    });
  };
  flat(features);
  return flattened;
};

/**
 * @consolidatePermissions
 *
 * Provides a utility which recursively iterates a list of all Features and returns a
 * normalized list of Permissions based on the Permissions available to all Features,
 * removing any duplicates.
 *
 * @param    {ApplicationFeature[]} features An Array of CalAmp iOn Features from which
 *           all permissions are to be retrieved and returned.
 * @returns  {Permission[]} A normalized list of Permissions available to all of the
 *           provided Features, with any and all duplicates removed.
 */
export const consolidatePermissions = (features: ApplicationFeature[]): Link[] => {
  let permissions: Link[] = [];
  const retrievePermissions = (items: ApplicationFeature[]) => {
    items.forEach((feature: ApplicationFeature) => {
      const children = feature.features;
      if (feature.enabled) {
        permissions = permissions.concat(feature.permissions);
        delete feature.permissions;
        if (children?.length) {
          retrievePermissions(children);
        }
      }
    });
  };
  retrievePermissions(features);
  return uniq(permissions, permission => permission.href);
};

/**
 * @normalizeFeatures
 *
 * Provides a utility which recursively iterates a list of Features and returns a normalized
 * list of Features which contain only the attributes necessary for adding enabled Features
 * to a Role's extended attributes.
 *
 * @param    {ApplicationFeature[]} features An Array of CalAmp iOn Features from which
 *           are to be normalized.
 * @returns  {ApplicationFeature[]} A normalized list of Features which contain only
 *           the attributes necessary when persisting to a Role.
 */
export const normalizeFeatures = (features: ApplicationFeature[]): ApplicationFeature[] | null => {
  const normalize = (items: ApplicationFeature[]) => {
    items.forEach((feature: ApplicationFeature) => {
      const children = feature.features;
      feature.allowed = feature.enabled;
      delete feature.permissions;
      delete feature.enabled;
      if (children?.length) {
        normalize(children);
      }
    });
  };
  normalize(features);
  return features.length ? features : null;
};

/**
 * @toFeatures
 *
 * Provides
 */
export const toFeatures = (
  allFeatures: ApplicationFeature[], selections: GenericObject): ApplicationFeature[] => {
  return allFeatures.filter((feature: ApplicationFeature) => {
    if (selections[feature.id]) {
      feature.enabled = selections[feature.id].enabled;
      if (selections[feature.id]?.modules) {
        feature.modules = selections[feature.id].modules;
      }
      const children = feature.features;
      if (children?.length) {
        feature.features = toFeatures(children, selections);
      }
      return feature;
    }
  });
};
